import React, { useState, useEffect, useMemo } from "react";
import { getBoardItems } from "../../services/mondayApiService";
import { BOARD_CONFIG } from "../../constants/boardConfig";
import {
  PROPERTIES_BOARD_ID,
  WEEKLY_CHECKS_BOARD_ID,
  METER_READINGS_BOARD_ID,
  TASKS_BOARD_ID,
  REPORTS_BOARD_ID,
  LAST_COMPLETED_DATE_COLUMN_ID,
  METER_READINGS_LAST_COMPLETED_DATE_COLUMN_ID,
} from "../../constants/columnIds";
import { useMondayContext } from "../../hooks/useMondayContext";

const MAINTENANCE_BOARD_ID = 5088283223;
import { parseColumnValue, isTaskDone } from "../../utils/dataParsingUtils";
import { normalizeDateOnly } from "../../utils/dateUtils";
import { formatDate } from "../../utils/dateUtils";
import { DONE_LABELS } from "../../constants/statusLabels";
import { updateItemName, updateDateColumn, createSubitem, deleteItem, updateTextColumn } from "../../services/mondayApiService";

const PropertyProfilePanel = ({ 
  isOpen, 
  onClose, 
  availableProperties, 
  propertyKeyMap,
  propertyNameToIdMap,
  onNewTask,
  onTaskClick,
}) => {
  // Access rawItems and propertyDetailsMap from context to reuse data from main app
  const { rawItems, rawItemsFetched, personColumnIdMap, propertyDetailsMap } = useMondayContext();
  
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);
  const [selectedPropertyName, setSelectedPropertyName] = useState("");
  const [loading, setLoading] = useState(false);
  const [editingSubtaskId, setEditingSubtaskId] = useState(null);
  const [editingSubtaskName, setEditingSubtaskName] = useState("");
  const [savingSubtask, setSavingSubtask] = useState(false);
  const [deletingSubtaskId, setDeletingSubtaskId] = useState(null);
  const [addingSubtaskToCheckId, setAddingSubtaskToCheckId] = useState(null);
  const [newSubtaskName, setNewSubtaskName] = useState("");
  const [creatingSubtask, setCreatingSubtask] = useState(false);
  const [editingReadingId, setEditingReadingId] = useState(null);
  const [editDueDate, setEditDueDate] = useState("");
  const [editWindowStart, setEditWindowStart] = useState("");
  const [editWindowEnd, setEditWindowEnd] = useState("");
  const [savingReading, setSavingReading] = useState(false);
  const [propertySearchText, setPropertySearchText] = useState("");
  const [showPropertyDropdown, setShowPropertyDropdown] = useState(false);
  const [propertyNotes, setPropertyNotes] = useState("");
  const [editingNotes, setEditingNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [propertyData, setPropertyData] = useState({
    weeklyChecks: [],
    meterReadings: [],
    tasks: [],
    reports: [],
    maintenance: [],
    keyNumber: null,
  });

  // Get property key number
  const keyNumber = useMemo(() => {
    if (!selectedPropertyId) return null;
    return propertyKeyMap[selectedPropertyId] || propertyKeyMap[String(selectedPropertyId)] || null;
  }, [selectedPropertyId, propertyKeyMap]);

  // Get property details (Keybox, Alarm, Door/Gate Code, Location)
  const propertyDetails = useMemo(() => {
    if (!selectedPropertyId) return null;
    const propertyIdStr = String(selectedPropertyId);
    return propertyDetailsMap[propertyIdStr] || propertyDetailsMap[Number(selectedPropertyId)] || null;
  }, [selectedPropertyId, propertyDetailsMap]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showPropertyDropdown && !event.target.closest('[data-property-selector]')) {
        setShowPropertyDropdown(false);
      }
    };
    if (showPropertyDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showPropertyDropdown]);

  // Fetch property data when property is selected
  useEffect(() => {
    if (!selectedPropertyId || !isOpen) return;

    const fetchPropertyData = async () => {
      setLoading(true);
      try {
        const allData = {
          weeklyChecks: [],
          meterReadings: [],
          tasks: [],
          reports: [],
          maintenance: [],
        };

        // Helper function to process items for a specific board
        const processBoardItems = (items, boardId, config) => {
          // Filter items linked to the selected property
          const propertyItems = items.filter(item => {
            const linkedItems = item.linked_items || [];
            return linkedItems.some(li => 
              String(li.id) === String(selectedPropertyId) || 
              Number(li.id) === Number(selectedPropertyId)
            );
          });

          // Process items based on board type
          for (const item of propertyItems) {
            const columnValues = item.column_values || [];
            const dueDateText = parseColumnValue(columnValues, config.dueDateColumnId);
            const statusText = parseColumnValue(columnValues, config.statusColumnId);
            const dueDate = dueDateText ? normalizeDateOnly(dueDateText) : null;

            const itemData = {
              id: item.id,
              name: item.name,
              dueDate,
              dueDateText,
              status: statusText,
              boardId,
              boardName: config.name,
              boardColor: config.color,
            };
            
            // Extract person names for Tasks board only
            if (boardId === TASKS_BOARD_ID) {
              const tasksPersonColumnId = personColumnIdMap[TASKS_BOARD_ID] || "dropdown_mky4syyj";
              const personCol = columnValues.find((c) => c.id === tasksPersonColumnId);
              let personNames = [];
              
              if (personCol) {
                // First, check the text field (Monday.com provides human-readable text)
                if (personCol.text) {
                  const textLabels = personCol.text.split(/[,\n]/).map((l) => l.trim()).filter(Boolean);
                  if (textLabels.length > 0) {
                    personNames = textLabels;
                  }
                }
                
                // Also try parsing the value JSON (Monday.com dropdown structure) as backup
                if (personNames.length === 0 && personCol.value && personCol.value !== "null" && personCol.value !== "") {
                  try {
                    const parsed = JSON.parse(personCol.value);
                    if (parsed.labels && Array.isArray(parsed.labels)) {
                      personNames = parsed.labels;
                    } else if (Array.isArray(parsed)) {
                      personNames = parsed;
                    } else if (parsed.label) {
                      personNames = [parsed.label];
                    }
                  } catch (e) {
                    // Not JSON, continue
                  }
                }
              }
              
              itemData.personNames = personNames;
            }

            if (boardId === WEEKLY_CHECKS_BOARD_ID) {
              const lastCompletedText = parseColumnValue(columnValues, LAST_COMPLETED_DATE_COLUMN_ID);
              const typeText = parseColumnValue(columnValues, config.typeColumnId);
              itemData.lastCompletedDate = lastCompletedText ? normalizeDateOnly(lastCompletedText) : null;
              itemData.lastCompletedDateText = lastCompletedText;
              itemData.type = typeText;
              // Store subtasks
              itemData.subtasks = item.subitems || [];
              allData.weeklyChecks.push(itemData);
            } else if (boardId === METER_READINGS_BOARD_ID) {
              const lastReadingText = parseColumnValue(columnValues, METER_READINGS_LAST_COMPLETED_DATE_COLUMN_ID);
              const windowStartText = parseColumnValue(columnValues, config.startDateColumnId);
              const windowEndText = parseColumnValue(columnValues, config.endDateColumnId);
              itemData.lastReadingDate = lastReadingText ? normalizeDateOnly(lastReadingText) : null;
              itemData.lastReadingDateText = lastReadingText;
              itemData.windowStart = windowStartText ? normalizeDateOnly(windowStartText) : null;
              itemData.windowStartText = windowStartText;
              itemData.windowEnd = windowEndText ? normalizeDateOnly(windowEndText) : null;
              itemData.windowEndText = windowEndText;
              allData.meterReadings.push(itemData);
            } else if (boardId === TASKS_BOARD_ID) {
              allData.tasks.push(itemData);
            } else if (boardId === REPORTS_BOARD_ID) {
              allData.reports.push(itemData);
            } else if (boardId === MAINTENANCE_BOARD_ID) {
              allData.maintenance.push(itemData);
            }
          }
        };

        // Determine which boards to fetch vs reuse from context
        const boardsToFetch = [];

        // Check if we can reuse data from context for Weekly Checks, Meter Readings, Tasks
        if (rawItemsFetched && rawItems && rawItems.length > 0) {
          // Process Weekly Checks from context
          const weeklyChecksConfig = BOARD_CONFIG[WEEKLY_CHECKS_BOARD_ID];
          if (weeklyChecksConfig) {
            const weeklyChecksItems = rawItems.filter(item => item._boardId === WEEKLY_CHECKS_BOARD_ID);
            if (weeklyChecksItems.length > 0) {
              processBoardItems(weeklyChecksItems, WEEKLY_CHECKS_BOARD_ID, weeklyChecksConfig);
            } else {
              boardsToFetch.push({ id: WEEKLY_CHECKS_BOARD_ID, config: weeklyChecksConfig });
            }
          }

          // Process Meter Readings from context
          const meterReadingsConfig = BOARD_CONFIG[METER_READINGS_BOARD_ID];
          if (meterReadingsConfig) {
            const meterReadingsItems = rawItems.filter(item => item._boardId === METER_READINGS_BOARD_ID);
            if (meterReadingsItems.length > 0) {
              processBoardItems(meterReadingsItems, METER_READINGS_BOARD_ID, meterReadingsConfig);
            } else {
              boardsToFetch.push({ id: METER_READINGS_BOARD_ID, config: meterReadingsConfig });
            }
          }

          // Process Tasks from context
          const tasksConfig = BOARD_CONFIG[TASKS_BOARD_ID];
          if (tasksConfig) {
            const tasksItems = rawItems.filter(item => item._boardId === TASKS_BOARD_ID);
            if (tasksItems.length > 0) {
              processBoardItems(tasksItems, TASKS_BOARD_ID, tasksConfig);
            } else {
              boardsToFetch.push({ id: TASKS_BOARD_ID, config: tasksConfig });
            }
          }
        } else {
          // If context data not available, fetch Weekly Checks, Meter Readings, Tasks
          const weeklyChecksConfig = BOARD_CONFIG[WEEKLY_CHECKS_BOARD_ID];
          if (weeklyChecksConfig) {
            boardsToFetch.push({ id: WEEKLY_CHECKS_BOARD_ID, config: weeklyChecksConfig });
          }
          const meterReadingsConfig = BOARD_CONFIG[METER_READINGS_BOARD_ID];
          if (meterReadingsConfig) {
            boardsToFetch.push({ id: METER_READINGS_BOARD_ID, config: meterReadingsConfig });
          }
          const tasksConfig = BOARD_CONFIG[TASKS_BOARD_ID];
          if (tasksConfig) {
            boardsToFetch.push({ id: TASKS_BOARD_ID, config: tasksConfig });
          }
        }

        // Always fetch Reports and Maintenance (not in context)
        const reportsConfig = BOARD_CONFIG[REPORTS_BOARD_ID];
        if (reportsConfig) {
          boardsToFetch.push({ id: REPORTS_BOARD_ID, config: reportsConfig });
        }
        const maintenanceConfig = BOARD_CONFIG[MAINTENANCE_BOARD_ID];
        if (maintenanceConfig) {
          boardsToFetch.push({ id: MAINTENANCE_BOARD_ID, config: maintenanceConfig });
        }

        // Fetch property Notes and remaining boards in parallel
        const PROPERTY_NOTES_COLUMN_ID = "text_mkyqss33";
        const fetchPromises = [];
        let boardPromises = [];

        // Add property Notes fetch to parallel batch
        fetchPromises.push(
          getBoardItems({
            boardIds: [PROPERTIES_BOARD_ID],
            columnIds: [PROPERTY_NOTES_COLUMN_ID],
          }).then(propertyItems => {
            const board = propertyItems?.[0];
            const items = board?.items_page?.items || [];
            const propertyItem = items.find(item => 
              String(item.id) === String(selectedPropertyId) || 
              Number(item.id) === Number(selectedPropertyId)
            );
            if (propertyItem) {
              const notesColumn = propertyItem.column_values?.find(c => c.id === PROPERTY_NOTES_COLUMN_ID);
              const notesText = notesColumn?.text || notesColumn?.value || "";
              setPropertyNotes(notesText);
              setEditingNotes(notesText);
            } else {
              setPropertyNotes("");
              setEditingNotes("");
            }
          }).catch(err => {
            console.error("Error fetching property notes:", err);
            setPropertyNotes("");
            setEditingNotes("");
          })
        );

        // Fetch remaining boards in parallel
        if (boardsToFetch.length > 0) {
          boardPromises = boardsToFetch.map(async ({ id: boardId, config }) => {
            const columnIds = [
              config.propertyColumnId,
              config.dueDateColumnId,
              config.statusColumnId,
            ];

            // Add board-specific columns
            if (boardId === WEEKLY_CHECKS_BOARD_ID) {
              columnIds.push(LAST_COMPLETED_DATE_COLUMN_ID, config.typeColumnId);
            } else if (boardId === METER_READINGS_BOARD_ID) {
              columnIds.push(METER_READINGS_LAST_COMPLETED_DATE_COLUMN_ID);
              // Add window start and end dates
              if (config.startDateColumnId) columnIds.push(config.startDateColumnId);
              if (config.endDateColumnId) columnIds.push(config.endDateColumnId);
            } else if (boardId === TASKS_BOARD_ID) {
              // Add person column for Tasks board
              const tasksPersonColumnId = personColumnIdMap[TASKS_BOARD_ID] || "dropdown_mky4syyj";
              columnIds.push(tasksPersonColumnId);
            }

            try {
              const boards = await getBoardItems({
                boardIds: [boardId],
                columnIds: columnIds.filter(Boolean),
                linkToItemColumnId: config.propertyColumnId,
                linkedBoardId: PROPERTIES_BOARD_ID,
              });

              const board = boards?.[0];
              const items = board?.items_page?.items || [];
              return { boardId, config, items, error: null };
            } catch (err) {
              console.error(`Error fetching data for board ${boardId}:`, err);
              return { boardId, config, items: [], error: err };
            }
          });

          // Add board fetch promises to the parallel batch
          boardPromises.forEach(promise => fetchPromises.push(promise));
        }

        // Wait for all fetches (property Notes + boards) to complete in parallel
        if (fetchPromises.length > 0) {
          await Promise.all(fetchPromises);
        }

        // Process board results (if any were fetched)
        if (boardPromises.length > 0) {
          const boardResults = await Promise.all(boardPromises);
          
          // Process results
          boardResults.forEach(({ boardId, config, items, error }) => {
            if (error) {
              // Error already logged, continue with other boards
              return;
            }
            processBoardItems(items, boardId, config);
          });
        }

        // Sort by due date (most recent first) - using a helper function for efficiency
        const sortByDueDate = (a, b) => {
          if (!a.dueDate && !b.dueDate) return 0;
          if (!a.dueDate) return 1;
          if (!b.dueDate) return -1;
          return b.dueDate.getTime() - a.dueDate.getTime();
        };
        
        allData.weeklyChecks.sort(sortByDueDate);
        allData.meterReadings.sort(sortByDueDate);
        allData.tasks.sort(sortByDueDate);
        allData.reports.sort(sortByDueDate);
        allData.maintenance.sort(sortByDueDate);

        setPropertyData(allData);
      } catch (err) {
        console.error("Error fetching property data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPropertyData();
    // Note: rawItems and rawItemsFetched are used inside the effect but not in deps
    // to avoid refetching when rawItems content changes. We only want to refetch
    // when selectedPropertyId or isOpen changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPropertyId, isOpen]);

  const handlePropertySelect = (propertyName) => {
    const propId = propertyNameToIdMap[propertyName];
    setSelectedPropertyId(propId);
    setSelectedPropertyName(propertyName);
    setPropertySearchText(propertyName);
    setShowPropertyDropdown(false);
    // Reset editing state when property changes
    setEditingSubtaskId(null);
    setEditingSubtaskName("");
    setEditingReadingId(null);
    setEditDueDate("");
    setEditWindowStart("");
    setEditWindowEnd("");
    setPropertyNotes(""); // Reset notes when property changes
    setEditingNotes(""); // Reset editing notes when property changes
    setIsEditingNotes(false); // Reset editing state when property changes
  };

  // Initialize property search text when property is selected
  useEffect(() => {
    if (selectedPropertyName && !propertySearchText) {
      setPropertySearchText(selectedPropertyName);
    }
  }, [selectedPropertyName]);

  // Filter properties based on search text
  const filteredProperties = useMemo(() => {
    if (!propertySearchText.trim()) {
      return availableProperties;
    }
    const searchLower = propertySearchText.toLowerCase();
    return availableProperties.filter(prop => 
      prop.name.toLowerCase().includes(searchLower)
    );
  }, [availableProperties, propertySearchText]);

  const handleEditSubtask = (subtaskId, currentName) => {
    setEditingSubtaskId(subtaskId);
    setEditingSubtaskName(currentName);
  };

  const handleCancelEditSubtask = () => {
    setEditingSubtaskId(null);
    setEditingSubtaskName("");
  };

  const handleSaveSubtask = async (subtaskId, parentItemId, boardId) => {
    if (!editingSubtaskName.trim()) {
      if (toast) toast.warning("Subtask name cannot be empty");
      return;
    }

    setSavingSubtask(true);
    try {
      await updateItemName({
        boardId: boardId,
        itemId: subtaskId,
        name: editingSubtaskName.trim(),
      });

      // Update the local state
      setPropertyData(prevData => ({
        ...prevData,
        weeklyChecks: prevData.weeklyChecks.map(check => {
          if (check.id === parentItemId) {
            return {
              ...check,
              subtasks: check.subtasks.map(subtask =>
                subtask.id === subtaskId
                  ? { ...subtask, name: editingSubtaskName.trim() }
                  : subtask
              ),
            };
          }
          return check;
        }),
      }));

      setEditingSubtaskId(null);
      setEditingSubtaskName("");
    } catch (err) {
      console.error("Error saving subtask:", err);
      if (toast) toast.error("Failed to save subtask. Please try again.");
    } finally {
      setSavingSubtask(false);
    }
  };

  const handleAddSubtask = (checkId) => {
    setAddingSubtaskToCheckId(checkId);
    setNewSubtaskName("");
  };

  const handleCancelAddSubtask = () => {
    setAddingSubtaskToCheckId(null);
    setNewSubtaskName("");
  };

  const handleCreateSubtask = async (checkId, boardId) => {
    if (!newSubtaskName.trim()) {
      if (toast) toast.warning("Subtask name cannot be empty");
      return;
    }

    setCreatingSubtask(true);
    try {
      const result = await createSubitem({
        itemId: checkId,
        subitemName: newSubtaskName.trim(),
      });

      // Update the local state
      setPropertyData(prevData => ({
        ...prevData,
        weeklyChecks: prevData.weeklyChecks.map(check => {
          if (check.id === checkId) {
            return {
              ...check,
              subtasks: [
                ...(check.subtasks || []),
                { id: result.id, name: result.name || newSubtaskName.trim() }
              ],
            };
          }
          return check;
        }),
      }));

      setAddingSubtaskToCheckId(null);
      setNewSubtaskName("");
    } catch (err) {
      console.error("Error creating subtask:", err);
      if (toast) toast.error("Failed to create subtask. Please try again.");
    } finally {
      setCreatingSubtask(false);
    }
  };

  const handleDeleteSubtask = async (subtaskId, parentItemId) => {
    if (!window.confirm("Are you sure you want to delete this subtask?")) {
      return;
    }

    setDeletingSubtaskId(subtaskId);
    try {
      await deleteItem({ itemId: subtaskId });

      // Update the local state
      setPropertyData(prevData => ({
        ...prevData,
        weeklyChecks: prevData.weeklyChecks.map(check => {
          if (check.id === parentItemId) {
            return {
              ...check,
              subtasks: check.subtasks.filter(subtask => subtask.id !== subtaskId),
            };
          }
          return check;
        }),
      }));
    } catch (err) {
      console.error("Error deleting subtask:", err);
      if (toast) toast.error("Failed to delete subtask. Please try again.");
    } finally {
      setDeletingSubtaskId(null);
    }
  };

  // Helper function to format date as YYYY-MM-DD for input fields
  const formatDateForInput = (date) => {
    if (!date) return "";
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return "";
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleEditReading = (reading) => {
    setEditingReadingId(reading.id);
    setEditDueDate(formatDateForInput(reading.dueDate));
    setEditWindowStart(formatDateForInput(reading.windowStart));
    setEditWindowEnd(formatDateForInput(reading.windowEnd));
  };

  const handleCancelEditReading = () => {
    setEditingReadingId(null);
    setEditDueDate("");
    setEditWindowStart("");
    setEditWindowEnd("");
  };

  const handleSaveNotes = async () => {
    if (!selectedPropertyId) return;
    
    setSavingNotes(true);
    try {
      const PROPERTY_NOTES_COLUMN_ID = "text_mkyqss33";
      await updateTextColumn({
        boardId: PROPERTIES_BOARD_ID,
        itemId: selectedPropertyId,
        columnId: PROPERTY_NOTES_COLUMN_ID,
        value: editingNotes.trim(),
      });
      setPropertyNotes(editingNotes.trim());
      setIsEditingNotes(false);
    } catch (err) {
      console.error("Error saving property notes:", err);
      if (toast) toast.error("Failed to save notes. Please try again.");
      // Revert to previous value on error
      setEditingNotes(propertyNotes);
    } finally {
      setSavingNotes(false);
    }
  };

  const handleCancelEditNotes = () => {
    setEditingNotes(propertyNotes);
    setIsEditingNotes(false);
  };

  const handleSaveReading = async (reading) => {
    setSavingReading(true);
    try {
      const meterReadingsConfig = BOARD_CONFIG[METER_READINGS_BOARD_ID];
      
      // Update due date
      if (editDueDate.trim()) {
        await updateDateColumn({
          boardId: METER_READINGS_BOARD_ID,
          itemId: reading.id,
          columnId: meterReadingsConfig.dueDateColumnId,
          date: editDueDate.trim(),
        });
      } else {
        // Clear due date if empty
        await updateDateColumn({
          boardId: METER_READINGS_BOARD_ID,
          itemId: reading.id,
          columnId: meterReadingsConfig.dueDateColumnId,
          date: null,
        });
      }

      // Update window start
      if (editWindowStart.trim()) {
        await updateDateColumn({
          boardId: METER_READINGS_BOARD_ID,
          itemId: reading.id,
          columnId: meterReadingsConfig.startDateColumnId,
          date: editWindowStart.trim(),
        });
      } else {
        await updateDateColumn({
          boardId: METER_READINGS_BOARD_ID,
          itemId: reading.id,
          columnId: meterReadingsConfig.startDateColumnId,
          date: null,
        });
      }

      // Update window end
      if (editWindowEnd.trim()) {
        await updateDateColumn({
          boardId: METER_READINGS_BOARD_ID,
          itemId: reading.id,
          columnId: meterReadingsConfig.endDateColumnId,
          date: editWindowEnd.trim(),
        });
      } else {
        await updateDateColumn({
          boardId: METER_READINGS_BOARD_ID,
          itemId: reading.id,
          columnId: meterReadingsConfig.endDateColumnId,
          date: null,
        });
      }

      // Update local state
      setPropertyData(prevData => ({
        ...prevData,
        meterReadings: prevData.meterReadings.map(r => {
          if (r.id === reading.id) {
            const updatedReading = { ...r };
            if (editDueDate.trim()) {
              updatedReading.dueDate = normalizeDateOnly(editDueDate.trim());
              updatedReading.dueDateText = editDueDate.trim();
            } else {
              updatedReading.dueDate = null;
              updatedReading.dueDateText = null;
            }
            if (editWindowStart.trim()) {
              updatedReading.windowStart = normalizeDateOnly(editWindowStart.trim());
              updatedReading.windowStartText = editWindowStart.trim();
            } else {
              updatedReading.windowStart = null;
              updatedReading.windowStartText = null;
            }
            if (editWindowEnd.trim()) {
              updatedReading.windowEnd = normalizeDateOnly(editWindowEnd.trim());
              updatedReading.windowEndText = editWindowEnd.trim();
            } else {
              updatedReading.windowEnd = null;
              updatedReading.windowEndText = null;
            }
            return updatedReading;
          }
          return r;
        }),
      }));

      setEditingReadingId(null);
      setEditDueDate("");
      setEditWindowStart("");
      setEditWindowEnd("");
    } catch (err) {
      console.error("Error saving meter reading:", err);
      if (toast) toast.error("Failed to save meter reading. Please try again.");
    } finally {
      setSavingReading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        width: "400px",
        height: "100%",
        backgroundColor: "#ffffff",
        borderRight: "1px solid #e6e9ef",
        display: "flex",
        flexDirection: "column",
        boxShadow: "2px 0 8px rgba(0,0,0,0.1)",
        flexShrink: 0,
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "16px",
          borderBottom: "1px solid #e6e9ef",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Property Profile</h2>
        <button
          onClick={onClose}
          style={{
            padding: "4px 8px",
            backgroundColor: "transparent",
            border: "none",
            cursor: "pointer",
            fontSize: "18px",
            color: "#676879",
          }}
        >
          ×
        </button>
      </div>

      {/* Property Selector */}
      <div style={{ padding: "16px", borderBottom: "1px solid #e6e9ef", position: "relative" }} data-property-selector>
        <label style={{ display: "block", marginBottom: "8px", fontSize: "12px", fontWeight: 600, color: "#323338" }}>
          Select Property
        </label>
        <div style={{ position: "relative" }}>
          <input
            type="text"
            value={propertySearchText}
            onChange={(e) => {
              setPropertySearchText(e.target.value);
              setShowPropertyDropdown(true);
            }}
            onFocus={() => setShowPropertyDropdown(true)}
            placeholder="Search or select a property..."
            style={{
              width: "100%",
              padding: "8px",
              border: "1px solid #e6e9ef",
              borderRadius: "4px",
              fontSize: "14px",
              boxSizing: "border-box",
            }}
          />
          {showPropertyDropdown && filteredProperties.length > 0 && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                backgroundColor: "#ffffff",
                border: "1px solid #e6e9ef",
                borderRadius: "4px",
                marginTop: "4px",
                maxHeight: "200px",
                overflowY: "auto",
                zIndex: 1000,
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              {filteredProperties.map((prop) => (
                <div
                  key={prop.id || prop.name}
                  onClick={() => handlePropertySelect(prop.name)}
                  style={{
                    padding: "8px",
                    cursor: "pointer",
                    fontSize: "14px",
                    borderBottom: "1px solid #f4f5f8",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#f4f5f8";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "#ffffff";
                  }}
                >
                  {prop.name}
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={{ marginTop: "8px", fontSize: "12px", color: "#676879" }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "center" }}>
            {keyNumber && <span>Key #: {keyNumber}</span>}
            {propertyDetails?.keybox && (
              <span>Keybox: {propertyDetails.keybox}</span>
            )}
            {propertyDetails?.alarm && (
              <span>Alarm: {propertyDetails.alarm}</span>
            )}
            {propertyDetails?.doorGateCode && (
              <span>Door/Gate Code: {propertyDetails.doorGateCode}</span>
            )}
            {propertyDetails?.location && (
              <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                Location:
                <button
                  onClick={() => {
                    const { lat, lng } = propertyDetails.location;
                    window.open(`https://www.google.com/maps?q=${lat},${lng}`, "_blank");
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#323338",
                    width: 14,
                    height: 14,
                  }}
                  title="Open in Google Maps"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ display: "block" }}
                  >
                    <path
                      d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
                      fill="currentColor"
                    />
                  </svg>
                </button>
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden", padding: "16px", boxSizing: "border-box" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#676879" }}>
            Loading...
          </div>
        ) : !selectedPropertyId ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#676879" }}>
            Select a property to view details
          </div>
        ) : (
          <div style={{ width: "100%", boxSizing: "border-box" }}>
            {/* Weekly Checks */}
            <div style={{ marginBottom: "24px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                <h3 style={{ margin: 0, fontSize: "14px", fontWeight: 600, color: "#323338" }}>
                  Weekly Checks
                </h3>
              </div>
              {propertyData.weeklyChecks.length === 0 ? (
                <div style={{ fontSize: "12px", color: "#a1a4b5", fontStyle: "italic" }}>No weekly checks</div>
              ) : (
                <div>
                  {propertyData.weeklyChecks.map((check) => (
                    <div
                      key={check.id}
                      style={{
                        padding: "8px",
                        marginBottom: "8px",
                        backgroundColor: "#e6e9ef",
                        borderRadius: "4px",
                        fontSize: "12px",
                      }}
                    >
                      {check.dueDateText && (
                        <div style={{ color: "#676879", marginBottom: "4px" }}>Due: {formatDate(check.dueDate)}</div>
                      )}
                      {check.lastCompletedDateText && (
                        <div style={{ color: "#676879" }}>Last Completed: {formatDate(check.lastCompletedDate)}</div>
                      )}
                      <div style={{ marginTop: "8px", paddingTop: "8px", borderTop: "1px solid #e6e9ef" }}>
                        {check.subtasks && check.subtasks.length > 0 && (
                          <>
                            {check.subtasks.map((subtask) => (
                            <div
                              key={subtask.id}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                marginBottom: "4px",
                                padding: "4px",
                              }}
                            >
                              {editingSubtaskId === subtask.id ? (
                                <>
                                  <input
                                    type="text"
                                    value={editingSubtaskName}
                                    onChange={(e) => setEditingSubtaskName(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        handleSaveSubtask(subtask.id, check.id, check.boardId);
                                      } else if (e.key === "Escape") {
                                        handleCancelEditSubtask();
                                      }
                                    }}
                                    style={{
                                      flex: 1,
                                      padding: "4px 6px",
                                      border: "1px solid #0073ea",
                                      borderRadius: "3px",
                                      fontSize: "11px",
                                    }}
                                    autoFocus
                                  />
                                  <button
                                    onClick={() => handleSaveSubtask(subtask.id, check.id, check.boardId)}
                                    disabled={savingSubtask}
                                    style={{
                                      padding: "4px 8px",
                                      backgroundColor: "#00c875",
                                      color: "white",
                                      border: "none",
                                      borderRadius: "3px",
                                      fontSize: "11px",
                                      cursor: savingSubtask ? "not-allowed" : "pointer",
                                      opacity: savingSubtask ? 0.6 : 1,
                                    }}
                                  >
                                    {savingSubtask ? "Saving..." : "Save"}
                                  </button>
                                  <button
                                    onClick={handleCancelEditSubtask}
                                    disabled={savingSubtask}
                                    style={{
                                      padding: "4px 8px",
                                      backgroundColor: "#a1a4b5",
                                      color: "white",
                                      border: "none",
                                      borderRadius: "3px",
                                      fontSize: "11px",
                                      cursor: savingSubtask ? "not-allowed" : "pointer",
                                    }}
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <>
                                  <span style={{ flex: 1, fontSize: "11px", color: "#323338" }}>
                                    • {subtask.name}
                                  </span>
                                  <button
                                    onClick={() => handleEditSubtask(subtask.id, subtask.name)}
                                    disabled={deletingSubtaskId === subtask.id}
                                    style={{
                                      padding: "2px 6px",
                                      backgroundColor: "transparent",
                                      color: "#0073ea",
                                      border: "1px solid #0073ea",
                                      borderRadius: "3px",
                                      fontSize: "10px",
                                      cursor: deletingSubtaskId === subtask.id ? "not-allowed" : "pointer",
                                      opacity: deletingSubtaskId === subtask.id ? 0.6 : 1,
                                    }}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => handleDeleteSubtask(subtask.id, check.id)}
                                    disabled={deletingSubtaskId === subtask.id}
                                    style={{
                                      padding: "2px 6px",
                                      backgroundColor: "transparent",
                                      color: "#d83a52",
                                      border: "1px solid #d83a52",
                                      borderRadius: "3px",
                                      fontSize: "10px",
                                      cursor: deletingSubtaskId === subtask.id ? "not-allowed" : "pointer",
                                      opacity: deletingSubtaskId === subtask.id ? 0.6 : 1,
                                    }}
                                  >
                                    {deletingSubtaskId === subtask.id ? "Deleting..." : "Remove"}
                                  </button>
                                </>
                              )}
                            </div>
                            ))}
                          </>
                        )}
                        {addingSubtaskToCheckId === check.id ? (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              marginTop: "4px",
                              padding: "4px",
                            }}
                          >
                            <input
                              type="text"
                              value={newSubtaskName}
                              onChange={(e) => setNewSubtaskName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  handleCreateSubtask(check.id, check.boardId);
                                } else if (e.key === "Escape") {
                                  handleCancelAddSubtask();
                                }
                              }}
                              placeholder="Enter task name..."
                              style={{
                                flex: 1,
                                padding: "4px 6px",
                                border: "1px solid #0073ea",
                                borderRadius: "3px",
                                fontSize: "11px",
                              }}
                              autoFocus
                              disabled={creatingSubtask}
                            />
                            <button
                              onClick={() => handleCreateSubtask(check.id, check.boardId)}
                              disabled={creatingSubtask || !newSubtaskName.trim()}
                              style={{
                                padding: "4px 8px",
                                backgroundColor: "#00c875",
                                color: "white",
                                border: "none",
                                borderRadius: "3px",
                                fontSize: "11px",
                                cursor: creatingSubtask || !newSubtaskName.trim() ? "not-allowed" : "pointer",
                                opacity: creatingSubtask || !newSubtaskName.trim() ? 0.6 : 1,
                              }}
                            >
                              {creatingSubtask ? "Adding..." : "Add"}
                            </button>
                            <button
                              onClick={handleCancelAddSubtask}
                              disabled={creatingSubtask}
                              style={{
                                padding: "4px 8px",
                                backgroundColor: "#a1a4b5",
                                color: "white",
                                border: "none",
                                borderRadius: "3px",
                                fontSize: "11px",
                                cursor: creatingSubtask ? "not-allowed" : "pointer",
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleAddSubtask(check.id)}
                            style={{
                              marginTop: "4px",
                              padding: "4px 8px",
                              backgroundColor: "transparent",
                              color: "#0073ea",
                              border: "1px solid #0073ea",
                              borderRadius: "3px",
                              fontSize: "11px",
                              cursor: "pointer",
                              width: "100%",
                            }}
                          >
                            + Add Task to Weekly Check
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Meter Readings */}
            <div style={{ marginBottom: "24px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}>
                  <h3 style={{ margin: 0, fontSize: "14px", fontWeight: 600, color: "#323338" }}>
                    Meter Readings
                  </h3>
                  {propertyData.meterReadings.length > 0 && propertyData.meterReadings[0].windowStartText && propertyData.meterReadings[0].windowEndText && (
                    <span style={{ fontSize: "11px", color: "#676879", fontWeight: 400 }}>
                      ({formatDate(propertyData.meterReadings[0].windowStart)} - {formatDate(propertyData.meterReadings[0].windowEnd)})
                    </span>
                  )}
                </div>
                {onTaskClick && propertyData.meterReadings.length > 0 && propertyData.meterReadings.some(r => r.lastReadingDateText) && (
                  <button
                    onClick={() => {
                      // Find the first meter reading with a last reading date
                      const readingWithLastReading = propertyData.meterReadings.find(r => r.lastReadingDateText);
                      if (readingWithLastReading) {
                        const task = {
                          id: readingWithLastReading.id,
                          name: readingWithLastReading.name,
                          boardId: readingWithLastReading.boardId,
                          date: readingWithLastReading.lastReadingDate || readingWithLastReading.dueDate,
                          propertyName: selectedPropertyName,
                          isCompletedEntry: true,
                        };
                        onTaskClick(task, selectedPropertyName);
                      }
                    }}
                    style={{
                      padding: "4px 8px",
                      backgroundColor: "#0073ea",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      fontSize: "12px",
                      cursor: "pointer",
                      fontWeight: 500,
                    }}
                  >
                    View Last Reading
                  </button>
                )}
              </div>
              {propertyData.meterReadings.length === 0 ? (
                <div style={{ fontSize: "12px", color: "#a1a4b5", fontStyle: "italic" }}>No meter readings</div>
              ) : (
                <div>
                  {propertyData.meterReadings.map((reading) => (
                    <div
                      key={reading.id}
                      style={{
                        padding: "8px",
                        marginBottom: "8px",
                        backgroundColor: "#e6e9ef",
                        borderRadius: "4px",
                        fontSize: "12px",
                        boxSizing: "border-box",
                        width: "100%",
                        overflow: "hidden",
                      }}
                    >
                      {editingReadingId === reading.id ? (
                        <div style={{ marginTop: "8px" }}>
                          <div style={{ marginBottom: "8px" }}>
                            <label style={{ display: "block", fontSize: "11px", color: "#676879", marginBottom: "4px" }}>
                              Due Date:
                            </label>
                            <input
                              type="date"
                              value={editDueDate}
                              onChange={(e) => setEditDueDate(e.target.value)}
                              style={{
                                width: "100%",
                                padding: "4px 6px",
                                border: "1px solid #0073ea",
                                borderRadius: "3px",
                                fontSize: "11px",
                                boxSizing: "border-box",
                              }}
                            />
                          </div>
                          <div style={{ marginBottom: "8px" }}>
                            <label style={{ display: "block", fontSize: "11px", color: "#676879", marginBottom: "4px" }}>
                              Window:
                            </label>
                            <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                              <input
                                type="date"
                                value={editWindowStart}
                                onChange={(e) => setEditWindowStart(e.target.value)}
                                style={{
                                  flex: 1,
                                  padding: "4px 6px",
                                  border: "1px solid #0073ea",
                                  borderRadius: "3px",
                                  fontSize: "11px",
                                  boxSizing: "border-box",
                                  minWidth: 0,
                                }}
                              />
                              <span style={{ fontSize: "11px", color: "#676879", padding: "0 4px" }}>to</span>
                              <input
                                type="date"
                                value={editWindowEnd}
                                onChange={(e) => setEditWindowEnd(e.target.value)}
                                min={editWindowStart || undefined}
                                style={{
                                  flex: 1,
                                  padding: "4px 6px",
                                  border: "1px solid #0073ea",
                                  borderRadius: "3px",
                                  fontSize: "11px",
                                  boxSizing: "border-box",
                                  minWidth: 0,
                                }}
                              />
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end", marginTop: "8px" }}>
                            <button
                              onClick={handleCancelEditReading}
                              disabled={savingReading}
                              style={{
                                padding: "4px 8px",
                                backgroundColor: "#a1a4b5",
                                color: "white",
                                border: "none",
                                borderRadius: "3px",
                                fontSize: "11px",
                                cursor: savingReading ? "not-allowed" : "pointer",
                              }}
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => handleSaveReading(reading)}
                              disabled={savingReading}
                              style={{
                                padding: "4px 8px",
                                backgroundColor: "#00c875",
                                color: "white",
                                border: "none",
                                borderRadius: "3px",
                                fontSize: "11px",
                                cursor: savingReading ? "not-allowed" : "pointer",
                                opacity: savingReading ? 0.6 : 1,
                              }}
                            >
                              {savingReading ? "Saving..." : "Save"}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                          <div>
                            <div style={{ color: "#676879" }}>
                              Due: {reading.dueDateText ? formatDate(reading.dueDate) : "Not Assigned"}
                            </div>
                            {reading.lastReadingDateText && (
                              <div style={{ color: "#676879" }}>Last Reading: {formatDate(reading.lastReadingDate)}</div>
                            )}
                          </div>
                          <button
                            onClick={() => handleEditReading(reading)}
                            style={{
                              padding: "2px 6px",
                              backgroundColor: "transparent",
                              color: "#0073ea",
                              border: "1px solid #0073ea",
                              borderRadius: "3px",
                              fontSize: "10px",
                              cursor: "pointer",
                              flexShrink: 0,
                            }}
                          >
                            Edit
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Tasks */}
            <div style={{ marginBottom: "24px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                <h3 style={{ margin: 0, fontSize: "14px", fontWeight: 600, color: "#323338" }}>
                  Tasks
                </h3>
                {onNewTask && selectedPropertyName && selectedPropertyId && (
                  <button
                    onClick={() => onNewTask(selectedPropertyId, selectedPropertyName)}
                    style={{
                      padding: "4px 8px",
                      backgroundColor: "#0073ea",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      fontSize: "12px",
                      cursor: "pointer",
                      fontWeight: 500,
                    }}
                  >
                    New Task
                  </button>
                )}
              </div>
              {propertyData.tasks.length === 0 ? (
                <div style={{ fontSize: "12px", color: "#a1a4b5", fontStyle: "italic" }}>No tasks</div>
              ) : (
                <div>
                  {/* Upcoming Tasks */}
                  {propertyData.tasks.filter(task => !isTaskDone(task.status, DONE_LABELS)).length > 0 && (
                    <div style={{ marginBottom: "16px" }}>
                      <h4 style={{ margin: "0 0 8px 0", fontSize: "12px", fontWeight: 600, color: "#323338" }}>
                        Upcoming Tasks
                      </h4>
                      {propertyData.tasks
                        .filter(task => !isTaskDone(task.status, DONE_LABELS))
                        .map((task) => (
                          <div
                            key={task.id}
                            onClick={() => {
                              if (onTaskClick) {
                                const taskObj = {
                                  id: task.id,
                                  name: task.name,
                                  boardId: task.boardId,
                                  date: task.dueDate,
                                  statusText: task.status,
                                  propertyName: selectedPropertyName,
                                  personNames: task.personNames || [],
                                };
                                onTaskClick(taskObj, selectedPropertyName);
                              }
                            }}
                            style={{
                              padding: "8px",
                              marginBottom: "8px",
                              backgroundColor: "#e6e9ef",
                              borderRadius: "4px",
                              fontSize: "12px",
                              cursor: onTaskClick ? "pointer" : "default",
                              transition: "background-color 0.2s",
                            }}
                            onMouseEnter={(e) => {
                              if (onTaskClick) {
                                e.currentTarget.style.backgroundColor = "#e6e9ef";
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (onTaskClick) {
                                e.currentTarget.style.backgroundColor = "#f4f5f8";
                              }
                            }}
                          >
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "4px" }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 600, marginBottom: "4px" }}>{task.name}</div>
                                {task.dueDateText && (
                                  <div style={{ color: "#676879" }}>Due: {formatDate(task.dueDate)}</div>
                                )}
                              </div>
                              {task.personNames && task.personNames.length > 0 && (
                                <div style={{ fontSize: "11px", color: "#676879", flexShrink: 0, marginLeft: "8px", textAlign: "right" }}>
                                  {task.personNames.join(", ")}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  )}

                  {/* Completed Tasks */}
                  {propertyData.tasks.filter(task => isTaskDone(task.status, DONE_LABELS)).length > 0 && (
                    <div>
                      <h4 style={{ margin: "0 0 8px 0", fontSize: "12px", fontWeight: 600, color: "#323338" }}>
                        Completed Tasks
                      </h4>
                      {propertyData.tasks
                        .filter(task => isTaskDone(task.status, DONE_LABELS))
                        .map((task) => (
                          <div
                            key={task.id}
                            onClick={() => {
                              if (onTaskClick) {
                                const taskObj = {
                                  id: task.id,
                                  name: task.name,
                                  boardId: task.boardId,
                                  date: task.dueDate,
                                  statusText: task.status,
                                  propertyName: selectedPropertyName,
                                  isCompletedEntry: true,
                                  done: true, // Mark as done so confirm button doesn't show
                                  personNames: task.personNames || [],
                                };
                                onTaskClick(taskObj, selectedPropertyName);
                              }
                            }}
                            style={{
                              padding: "8px",
                              marginBottom: "8px",
                              backgroundColor: "#e6e9ef",
                              borderRadius: "4px",
                              fontSize: "12px",
                              cursor: onTaskClick ? "pointer" : "default",
                              transition: "background-color 0.2s",
                              opacity: 0.6,
                            }}
                            onMouseEnter={(e) => {
                              if (onTaskClick) {
                                e.currentTarget.style.backgroundColor = "#e6e9ef";
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (onTaskClick) {
                                e.currentTarget.style.backgroundColor = "#f4f5f8";
                              }
                            }}
                          >
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "4px" }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 600, marginBottom: "4px" }}>{task.name}</div>
                                {task.dueDateText && (
                                  <div style={{ color: "#676879" }}>Completed: {formatDate(task.dueDate)}</div>
                                )}
                              </div>
                              {task.personNames && task.personNames.length > 0 && (
                                <div style={{ fontSize: "11px", color: "#676879", flexShrink: 0, marginLeft: "8px", textAlign: "right" }}>
                                  {task.personNames.join(", ")}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Reports */}
            <div style={{ marginBottom: "24px" }}>
              <h3 style={{ margin: "0 0 12px 0", fontSize: "14px", fontWeight: 600, color: "#323338" }}>
                Reports
              </h3>
              {propertyData.reports.length === 0 ? (
                <div style={{ fontSize: "12px", color: "#a1a4b5", fontStyle: "italic" }}>No reports</div>
              ) : (
                <div>
                  {propertyData.reports.map((report) => (
                    <div
                      key={report.id}
                      style={{
                        padding: "8px",
                        marginBottom: "8px",
                        backgroundColor: "#e6e9ef",
                        borderRadius: "4px",
                        fontSize: "12px",
                      }}
                    >
                      <div style={{ fontWeight: 600, marginBottom: "4px" }}>{report.name}</div>
                      {report.dueDateText && (
                        <div style={{ color: "#676879" }}>Due: {formatDate(report.dueDate)}</div>
                      )}
                      <div style={{ color: "#676879" }}>Status: {report.status || "N/A"}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Maintenance */}
            <div style={{ marginBottom: "24px" }}>
              <h3 style={{ margin: "0 0 12px 0", fontSize: "14px", fontWeight: 600, color: "#323338" }}>
                Maintenance
              </h3>
              {propertyData.maintenance.length === 0 ? (
                <div style={{ fontSize: "12px", color: "#a1a4b5", fontStyle: "italic" }}>No maintenance</div>
              ) : (
                <div>
                  {propertyData.maintenance.map((maintenance) => (
                    <div
                      key={maintenance.id}
                      style={{
                        padding: "8px",
                        marginBottom: "8px",
                        backgroundColor: "#e6e9ef",
                        borderRadius: "4px",
                        fontSize: "12px",
                      }}
                    >
                      <div style={{ fontWeight: 600, marginBottom: "4px" }}>{maintenance.name}</div>
                      {maintenance.dueDateText && (
                        <div style={{ color: "#676879" }}>Due: {formatDate(maintenance.dueDate)}</div>
                      )}
                      <div style={{ color: "#676879" }}>Status: {maintenance.status || "N/A"}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Notes */}
            <div style={{ marginBottom: "24px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                <h3 style={{ margin: 0, fontSize: "14px", fontWeight: 600, color: "#323338" }}>
                  Notes
                </h3>
              </div>
              {isEditingNotes ? (
                <div>
                  <textarea
                    value={editingNotes}
                    onChange={(e) => setEditingNotes(e.target.value)}
                    placeholder="Add notes about this property..."
                    disabled={savingNotes}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                        handleSaveNotes();
                      } else if (e.key === "Escape") {
                        handleCancelEditNotes();
                      }
                    }}
                    style={{
                      width: "100%",
                      minHeight: "100px",
                      padding: "8px",
                      border: "1px solid #0073ea",
                      borderRadius: "4px",
                      fontSize: "12px",
                      fontFamily: "inherit",
                      resize: "vertical",
                      boxSizing: "border-box",
                      backgroundColor: savingNotes ? "#f4f5f8" : "#ffffff",
                      color: "#323338",
                    }}
                    autoFocus
                  />
                  <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "8px" }}>
                    <button
                      onClick={handleCancelEditNotes}
                      disabled={savingNotes}
                      style={{
                        padding: "4px 8px",
                        backgroundColor: "#a1a4b5",
                        color: "white",
                        border: "none",
                        borderRadius: "3px",
                        fontSize: "11px",
                        cursor: savingNotes ? "not-allowed" : "pointer",
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveNotes}
                      disabled={savingNotes || editingNotes === propertyNotes}
                      style={{
                        padding: "4px 8px",
                        backgroundColor: savingNotes || editingNotes === propertyNotes ? "#a1a4b5" : "#00c875",
                        color: "white",
                        border: "none",
                        borderRadius: "3px",
                        fontSize: "11px",
                        cursor: savingNotes || editingNotes === propertyNotes ? "not-allowed" : "pointer",
                        opacity: savingNotes || editingNotes === propertyNotes ? 0.6 : 1,
                      }}
                    >
                      {savingNotes ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
                  <div style={{ flex: 1, fontSize: "12px", color: propertyNotes ? "#323338" : "#a1a4b5", fontStyle: propertyNotes ? "normal" : "italic", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {propertyNotes || "No notes"}
                  </div>
                  <button
                    onClick={() => {
                      setEditingNotes(propertyNotes);
                      setIsEditingNotes(true);
                    }}
                    style={{
                      padding: "2px 6px",
                      backgroundColor: "transparent",
                      color: "#0073ea",
                      border: "1px solid #0073ea",
                      borderRadius: "3px",
                      fontSize: "10px",
                      cursor: "pointer",
                      flexShrink: 0,
                    }}
                  >
                    Edit
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertyProfilePanel;

